import { useState } from "react";
import { timeManagementSchema, TIME_MGMT_FORMATS, timeMgmtFormatLabels, type TimeManagementPayload } from "@anton/shared";
import { PageHeader } from "../components/PageHeader";
import { Field } from "../components/Field";
import { RadioGroup } from "../components/RadioGroup";
import { SubmitButton } from "../components/SubmitButton";
import { SuccessScreen } from "../components/SuccessScreen";
import { Card, Lead, P, H3, CheckList, SectionHeader, Stat } from "../components/Section";
import { submitApplication } from "../lib/api";
import { haptic, showAlert } from "../lib/telegram";

export default function TimeManagement() {
  const [form, setForm] = useState<Partial<TimeManagementPayload>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (done) {
    return (
      <SuccessScreen message="Заявка принята. Скоро напишет мой ассистент Алёна — согласует оплату и дату первой встречи." />
    );
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = timeManagementSchema.safeParse(form);
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      for (const i of parsed.error.issues) fe[i.path.join(".")] = i.message;
      setErrors(fe);
      haptic("error");
      return;
    }
    setLoading(true);
    const res = await submitApplication("TIME_MANAGEMENT", parsed.data);
    setLoading(false);
    if (!res.ok) { showAlert(res.error ?? "Ошибка"); haptic("error"); return; }
    setDone(true);
  }

  return (
    <div className="pb-10">
      <PageHeader
        eyebrow="Личный консалтинг"
        title="Система тайм-менеджмента «Как у Антона»"
        subtitle="3 встречи. Уйдёшь с настроенным Todoist, понятным финансовым планом жизни и рабочим ассистентом."
        icon="🕘"
        iconColor="orange"
      />

      <div className="px-5 mt-3 space-y-3">
        <Card>
          <Lead>
            Это не курс «как стать продуктивным». Это упакованная за 5 лет система,
            которой я веду свои бизнесы, отношения и здоровье — без выгорания и хаоса.
          </Lead>
        </Card>

        <Card>
          <P>
            После 3 встреч у тебя будет настроенный Todoist в моём формате, шаблон финансового плана жизни,
            набор регламентов для ассистента и конкретный план, как делегировать так, чтобы получать результат с первого раза.
          </P>
        </Card>

        <div className="grid grid-cols-3 gap-3">
          <Stat value="3" label="встречи" />
          <Stat value="3" label="модуля" />
          <Stat value="17" label="готовых шаблонов" />
        </div>

        {/* Модуль 1 */}
        <Card>
          <div className="flex items-center gap-3 mb-3">
            <div className="icon-tile bg-[var(--icon-blue-bg)] text-[var(--icon-blue)]">1</div>
            <H3>Основы и настройка Todoist</H3>
          </div>
          <CheckList items={[
            "Как использовать своё время максимально эффективно и не выгорать",
            "Постановка задач, приоритеты, эффективное делегирование",
            "Все ключевые функции Todoist — для скорости",
            "Практика: настройка Todoist «Как у Антона» на телефоне и компьютере",
          ]} />
        </Card>

        {/* Модуль 2 */}
        <Card>
          <div className="flex items-center gap-3 mb-3">
            <div className="icon-tile bg-[var(--icon-purple-bg)] text-[var(--icon-purple)]">2</div>
            <H3>Цели и баланс жизни</H3>
          </div>
          <CheckList items={[
            "Как ставить цели, чтобы 1 января не открыть список и не охренеть от несделанного",
            "Метод «дня стратегии» каждый месяц — планомерно приближаешься к целям",
            "Готовый Excel-шаблон: цели по всем сферам жизни + финансовый план мечты",
            "Чит-коды баланса: бизнес, отношения, тело — внедрял у себя 5 лет",
          ]} />
        </Card>

        {/* Модуль 3 */}
        <Card>
          <div className="flex items-center gap-3 mb-3">
            <div className="icon-tile bg-[var(--icon-green-bg)] text-[var(--icon-green)]">3</div>
            <H3>Личный ассистент за 5 дней</H3>
          </div>
          <CheckList items={[
            "Где и как искать ассистента (готовое объявление — шаблон)",
            "Как провести собеседование и тестирование, чтобы не менять каждые 3 месяца",
            "Готовое тестовое задание — мой проверенный вариант",
            "Должностная инструкция + ожидания + KPI",
            "17 регламентов — от плана/отчёта до организации путешествий",
            "Адаптация в должность за 5 дней",
          ]} />
        </Card>

        <Card>
          <H3>Что будет после</H3>
          <P>
            После курса остаюсь на связи в Telegram — отвечаю на вопросы по внедрению.
            Это не «купил-получил», а «купил-внедрил-работает».
          </P>
        </Card>

        <Card>
          <H3>Формат</H3>
          <CheckList items={[
            "3 встречи — лично (если на Бали) или Zoom",
            "Старт сразу после оплаты, дату первой встречи согласует Алёна",
            "Все материалы и шаблоны — пожизненный доступ",
          ]} />
        </Card>

        <div className="ios-callout-muted">
          Стоимость — по заявке. Скажет мой ассистент Алёна, когда свяжется.
        </div>
      </div>

      <SectionHeader>Оставить заявку</SectionHeader>
      <form noValidate onSubmit={onSubmit} className="px-5 space-y-4">
        <Field label="Имя" error={errors["name"]}>
          <input className={`ios-input ${errors["name"] ? "ios-input-error" : ""}`}
            value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Контакт (Telegram или телефон)" error={errors["contact"]}>
          <input className={`ios-input ${errors["contact"] ? "ios-input-error" : ""}`}
            value={form.contact ?? ""} onChange={(e) => setForm({ ...form, contact: e.target.value })} />
        </Field>
        <Field label="Формат встреч" error={errors["format"]}>
          <RadioGroup
            value={form.format}
            onChange={(v) => setForm({ ...form, format: v as TimeManagementPayload["format"] })}
            options={TIME_MGMT_FORMATS.map((v) => ({ value: v, label: timeMgmtFormatLabels[v] }))}
          />
        </Field>
        {form.format === "IN_PERSON" && (
          <Field label="Где удобно встречаться?" error={errors["inPersonLocation"]}>
            <input className={`ios-input ${errors["inPersonLocation"] ? "ios-input-error" : ""}`}
              value={form.inPersonLocation ?? ""}
              onChange={(e) => setForm({ ...form, inPersonLocation: e.target.value })}
              placeholder="город или конкретное место" />
          </Field>
        )}
        <div className="pt-2">
          <SubmitButton loading={loading}>Отправить заявку</SubmitButton>
        </div>
      </form>
    </div>
  );
}
